import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useChat } from '../../context/ChatContext';
import { usePermissions } from '../../context/PermissionsContext';
import { isPrivilegedRole, rolExcluido } from '../../config/comingSoonRoutes';
import axiosInstance from '../../utils/axiosConfig';
import { Modal } from '../common';
import { FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import '../Modal.css';
import {
  Users,
  Laptop,
  Smartphone,
  Tablet,
  Printer,
  Server,
  Network,
  AlertTriangle,
  ChevronDown,
  Phone,
  Wrench,
  Cpu,
  Activity,
  Layers,
  User,
  LogOut,
  Briefcase,
  GitGraph,
  Home,
  Calendar,
  Bell,
  Shield,
  Megaphone,
  FileText,
  FileCheck,
  KeyRound,
  Lock,
  Settings,
  Car,
  BedDouble,
  Receipt,
  Package,
  CalendarClock,
  GraduationCap,
  ClipboardList,
  MessageCircle,
  Clock,
  Plane,
  Ticket,
  Mic,
  Building2,
  ShoppingCart,
  ShieldCheck,
  UtensilsCrossed,
  PenLine,
  ShoppingBag,
  KanbanSquare,
  BookOpen,
  Truck,
  Fingerprint,
  DoorOpen,
  ParkingSquare
} from 'lucide-react';
import logo from '../centrosurablanco.png';
import DescargasEscritorio, { hayDescargas } from './DescargasEscritorio';

// ── Colapso de la sidebar ──────────────────────────────────────────────
const FULL_WIDTH = 256;
const RAIL_WIDTH = 72;
const COLLAPSE_STORAGE_KEY = 'sidebar-collapsed';
// Curva con salida suave: arranca decidido y se asienta sin rebote.
const COLLAPSE_EASE = [0.32, 0.72, 0, 1];
const COLLAPSE_TRANSITION = { duration: 0.34, ease: COLLAPSE_EASE };

// Logo (centrosurablanco.png, 542x295). En el riel se muestra solo la "S",
// que ocupa x 13-153 / y 69-280 del archivo. Ventana y desplazamiento en px.
const LOGO_SRC = { w: 542, h: 295 };
const LOGO_S_BOX = { x: 13, y: 69, w: 140, h: 211 };
const LOGO_FULL_HEIGHT = 96;
const LOGO_MARK_HEIGHT = 36;
const LOGO_FULL = {
  frame: { width: (LOGO_SRC.w * LOGO_FULL_HEIGHT) / LOGO_SRC.h, height: LOGO_FULL_HEIGHT },
  img: { height: LOGO_FULL_HEIGHT, x: 0, y: 0 },
};
const markScale = LOGO_MARK_HEIGHT / LOGO_S_BOX.h;
const LOGO_MARK = {
  frame: { width: LOGO_S_BOX.w * markScale, height: LOGO_MARK_HEIGHT },
  img: { height: LOGO_SRC.h * markScale, x: -LOGO_S_BOX.x * markScale, y: -LOGO_S_BOX.y * markScale },
};

// Cambia el contenido de una zona con un fundido corto. El contenido saliente
// se va rápido; el entrante espera un poco (más al expandir, para que el texto
// aparezca cuando ya hay ancho y no se vea cortado).
const Swap = ({ swapKey, delayIn = 0, className, children }) => (
  <AnimatePresence initial={false} mode="wait">
    <motion.div
      key={swapKey}
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.18, delay: delayIn, ease: 'easeOut' } }}
      exit={{ opacity: 0, transition: { duration: 0.08, ease: 'easeIn' } }}
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

// Anima la altura hacia la de su contenido, para que al cambiar de layout
// (fila horizontal ↔ columna vertical) lo de abajo se desplace suave y no salte.
const AutoHeight = ({ className, children }) => {
  const innerRef = useRef(null);
  const [height, setHeight] = useState('auto');

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setHeight(entry.contentRect.height));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      className={className}
      initial={false}
      animate={{ height }}
      transition={COLLAPSE_TRANSITION}
      style={{ overflow: 'hidden' }}
    >
      <div ref={innerRef}>{children}</div>
    </motion.div>
  );
};

// Tooltip a la derecha del riel. Va por portal porque la sidebar recorta su
// contenido (overflow hidden) y un tooltip normal quedaría cortado.
// Botones de la fila de herramientas (sobre el panel del usuario): todos del mismo tamaño
const BOTON_HERRAMIENTA = 'w-9 h-9 flex items-center justify-center rounded-lg text-white/40 hover:bg-white/5 active:scale-90 transition-[color,background-color,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60';

const RailTooltip = ({ label, enabled = true, children }) => {
  const [rect, setRect] = useState(null);

  // El wrapper se renderiza siempre (aunque esté deshabilitado) para no remontar
  // el hijo al alternar: así el botón conserva el foco del teclado.
  useEffect(() => {
    if (!enabled) setRect(null);
  }, [enabled]);

  const show = (e) => {
    if (enabled) setRect(e.currentTarget.getBoundingClientRect());
  };
  const hide = () => setRect(null);

  return (
    <>
      <div
        className="flex justify-center"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onMouseDown={hide}
      >
        {children}
      </div>
      {createPortal(
        <AnimatePresence>
          {enabled && rect && (
            <div
              className="fixed z-[9999] pointer-events-none"
              style={{ top: rect.top + rect.height / 2, left: rect.right + 10, transform: 'translateY(-50%)' }}
            >
              <motion.div
                role="tooltip"
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -4 }}
                transition={{ duration: 0.14, ease: 'easeOut' }}
                className="whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg shadow-black/30 ring-1 ring-white/10"
              >
                {label}
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

// Burger de 3 líneas. Expandida, la línea del medio queda corta (pista de
// "recoger"); colapsada, las tres líneas se igualan.
const BurgerIcon = ({ collapsed }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <motion.line
      x1="2.5" y1="4.5" y2="4.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"
      initial={false} animate={{ x2: 15.5 }} transition={COLLAPSE_TRANSITION}
    />
    <motion.line
      x1="2.5" y1="9" y2="9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"
      initial={false} animate={{ x2: collapsed ? 15.5 : 10 }} transition={COLLAPSE_TRANSITION}
    />
    <motion.line
      x1="2.5" y1="13.5" y2="13.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"
      initial={false} animate={{ x2: 15.5 }} transition={COLLAPSE_TRANSITION}
    />
  </svg>
);

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const { unreadCount: chatUnreadCount } = useChat();
  const { hasModuleAccess, isModuleHidden, isModuleMuyPronto } = usePermissions();

  // Change password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  const openPasswordModal = useCallback(() => {
    setPasswordForm({ current: '', new: '', confirm: '' });
    setPasswordError('');
    setPasswordSuccess(false);
    setShowPasswordModal(true);
  }, []);

  const handleChangePassword = useCallback(async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordForm.new.length < 4) {
      setPasswordError('La nueva contraseña debe tener al menos 4 caracteres');
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }

    setPasswordLoading(true);
    try {
      await axiosInstance.post('/api/auth/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.new,
      });
      setPasswordSuccess(true);
      setTimeout(() => setShowPasswordModal(false), 1500);
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Error al cambiar la contraseña');
    } finally {
      setPasswordLoading(false);
    }
  }, [passwordForm]);

  const [expandedGroups, setExpandedGroups] = useState({
    'gestion-humana': false,
    'tecnologia': false,
    'seguridad': false,
    'mercadeo': false,
    'servicios-generales': false,
    'procura': false,
    'calidad': false,
    'utilidades': false
  });

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Sidebar colapsada (riel de iconos). Se recuerda entre sesiones.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // Sin almacenamiento disponible: el estado solo dura esta sesión.
    }
  }, [collapsed]);

  // Desde el riel, tocar el icono de un grupo expande la sidebar con ese grupo abierto.
  const openGroupFromRail = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: true }));
    setCollapsed(false);
  };

  const isPathActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const menuStructure = [
    {
      key: 'utilidades',
      title: 'Herramientas',
      icon: Layers,
      roles: ['admin', 'ti', 'gh', 'captacion', 'user', 'recepcion', 'cxp', 'costos', 'mercadeo', 'contabilidad'],
      items: [
        { path: '/proyectos', icon: KanbanSquare, label: 'Proyectos', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
        { path: '/documentos', icon: FileText, label: 'Documentos', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
        { path: '/reserva-salas', icon: Calendar, label: 'Reserva de Salas', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
        { path: '/grabacion-reuniones', icon: Mic, label: 'Minutas Inteligentes', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
        { path: '/solicitud-taxi', icon: Car, label: 'Solicitud de Taxi', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
        { path: '/solicitud-hospedaje', icon: BedDouble, label: 'Solicitud de Hospedaje', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
        { path: '/solicitud-viaticos', icon: Receipt, label: 'Solicitud de Viáticos', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion', 'cxp'] },
        { path: '/solicitud-comida', icon: UtensilsCrossed, label: 'Solicitud de Comida (CDN)', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
        { path: '/calendario-cierre', icon: CalendarClock, label: 'Calendario de Cierre', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion', 'contabilidad'] },
        { path: '/productos-sura', icon: Package, label: 'Productos SURA', roles: ['admin', 'ti', 'gh', 'captacion', 'user', 'recepcion', 'cxp', 'costos', 'mercadeo', 'contabilidad'] },
        { path: '/estacionamiento', icon: ParkingSquare, label: 'Estacionamiento (CA1)', roles: ['admin', 'ti', 'gh', 'captacion', 'user', 'recepcion', 'cxp', 'costos', 'mercadeo', 'contabilidad'] },
      ]
    },
    {
      key: 'gestion-humana',
      title: 'Gestión Humana',
      icon: Users,
      roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'],
      items: [
        { path: '/empleados', icon: Users, label: 'Empleados', roles: ['admin', 'gh', 'captacion'] },
        { path: '/vacantes', icon: Briefcase, label: 'Vacantes', roles: ['admin', 'gh', 'captacion'] },
        { path: '/organigrama', icon: GitGraph, label: 'Estructura Organizativa', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
        { path: '/asistencia', icon: Fingerprint, label: 'Asistencia', roles: ['admin'] },
        { path: '/constancia-trabajo', icon: FileCheck, label: 'Constancia de Trabajo', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
        { path: '/solicitud-permisos', icon: Clock, label: 'Solicitud de Permiso', roles: ['admin', 'gh', 'user', 'captacion', 'recepcion'] },
        { path: '/solicitud-vacaciones', icon: Plane, label: 'Solicitud de Vacaciones', roles: ['admin', 'gh', 'user', 'captacion', 'recepcion'] },
        { path: '/firmas', icon: PenLine, label: 'Firmas Digitales', roles: ['admin'] },
      ]
    },
    {
      key: 'mercadeo',
      title: 'Mercadeo',
      icon: Megaphone,
      roles: ['admin', 'gh', 'captacion', 'user', 'recepcion', 'mercadeo'],
      items: [
        { path: '/encuestas-producto', icon: ClipboardList, label: 'Encuesta de Productos', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion', 'mercadeo'] },
        { path: '/academia-sura', icon: GraduationCap, label: 'Academia SURA', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion', 'mercadeo'] },
      ]
    },
    {
      key: 'tecnologia',
      title: 'Tecnología e Innovación',
      icon: Cpu,
      roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'],
      items: [
        { path: '/soporte-ti', icon: Ticket, label: 'Tickets', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
        { path: '/equipos', icon: Laptop, label: 'Equipos', roles: ['admin'] },
        { path: '/smartphones', icon: Smartphone, label: 'Smartphones', roles: ['admin'] },
        { path: '/tablets', icon: Tablet, label: 'Tablets', roles: ['admin'] },
        { path: '/impresoras', icon: Printer, label: 'Impresoras', roles: ['admin'] },
        { path: '/servidores', icon: Server, label: 'Servidores', roles: ['admin'] },
        { path: '/redes', icon: Network, label: 'Redes', roles: ['admin'] },
        // { path: '/mantenimientos', icon: Wrench, label: 'Mantenimientos', roles: ['admin'] },
        { path: '/eventos', icon: AlertTriangle, label: 'Eventos', roles: ['admin'] },
        { path: '/credenciales', icon: KeyRound, label: 'Credenciales', roles: ['admin'] },
        { path: '/presupuestos', icon: Activity, label: 'Presupuestos', roles: ['admin'] },
      ]
    },
    {
      key: 'seguridad',
      title: 'Seguridad y PCP',
      icon: Shield,
      roles: ['admin'],
      items: [
        { path: '/talleres-cursos', icon: GraduationCap, label: 'Talleres y Cursos', roles: ['admin'] },
        { path: '/puertas', icon: DoorOpen, label: 'Puertas', roles: ['admin'] },
      ]
    },
    {
      key: 'servicios-generales',
      title: 'Servicios Generales',
      icon: Building2,
      roles: ['admin', 'gh', 'captacion', 'user', 'recepcion', 'generales'],
      items: [
        { path: '/solicitud-servicio', icon: Wrench, label: 'Solicitud de Servicio', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion', 'generales'] },
        { path: '/bitacora', icon: BookOpen, label: 'Bitácora', roles: ['admin', 'generales'] },
        { path: '/equipos-moviles', icon: Truck, label: 'Equipos Móviles', roles: ['admin', 'generales'] },
      ]
    },
    {
      key: 'procura',
      title: 'Procura',
      icon: ShoppingCart,
      roles: ['admin', 'procura'],
      items: [
        { path: '/requisicion-compras', icon: ShoppingBag, label: 'Requisición a Compras', roles: ['admin', 'procura'] },
        { path: '/solicitud-papeleria', icon: PenLine, label: 'Solicitud de Papelería', roles: ['admin', 'procura'] },
      ]
    },
    {
      key: 'calidad',
      title: 'Calidad',
      icon: ShieldCheck,
      roles: ['admin', 'ti', 'gh', 'captacion', 'user', 'recepcion', 'cxp', 'costos', 'mercadeo', 'contabilidad', 'procura', 'seguridad', 'generales', 'finanzas'],
      items: [
        { path: '/sura-reporta', icon: ShieldCheck, label: 'Sura Reporta', roles: ['admin', 'ti', 'gh', 'captacion', 'user', 'recepcion', 'cxp', 'costos', 'mercadeo', 'contabilidad', 'procura', 'seguridad', 'generales', 'finanzas'] },
      ]
    }
  ];

  const quickAccessItems = [
    { path: '/inicio', icon: Home, label: 'Inicio', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
    { path: '/extensions-directory', icon: Phone, label: 'Directorio', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'] },
    { path: '/chat', icon: MessageCircle, label: 'Chat', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'], hasBadge: true, badgeCount: chatUnreadCount },
    { path: '/notificaciones', icon: Bell, label: 'Notificaciones', roles: ['admin', 'gh', 'captacion', 'user', 'recepcion'], hasBadge: true, badgeCount: unreadCount },
  ];

  const hasPermission = (itemRoles, path) => {
    if (!user?.role) return false;
    // Módulos ocultos por admin: nadie los ve en el sidebar (ni siquiera admin/ti).
    if (path && isModuleHidden(path)) return false;
    if (path && rolExcluido(user.role, path)) return false;
    if (isPrivilegedRole(user.role)) return true;
    if (path) return hasModuleAccess(user, path);
    return itemRoles.includes(user.role);
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: 'Administrador',
      ti: 'Tecnología e Innovación',
      gh: 'Gestión Humana',
      'captacion': 'GH Captación',
      costos: 'Costos y Presupuesto',
      recepcion: 'Recepción',
      cxp: 'Cuentas por Pagar',
      mercadeo: 'Mercadeo',
      contabilidad: 'Contabilidad',
      user: 'Usuario'
    };
    return roles[role] || role;
  };

  // Botón de acceso rápido. Se usa en la fila (expandida) y en la columna del riel;
  // cada layout lleva su propio layoutId para que el indicador no salte entre ambos.
  const renderQuickAccessLink = (item, indicatorId, withTitle) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const showBadge = item.hasBadge && (item.badgeCount ?? 0) > 0;

    return (
      <Link
        to={item.path}
        title={withTitle ? item.label : undefined}
        aria-label={item.label}
        className="relative z-10 p-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
      >
        {/* Sliding indicator with Framer Motion */}
        {isActive && (
          <motion.div
            layoutId={indicatorId}
            className="absolute inset-0 bg-cyan-500 rounded-lg shadow-lg shadow-cyan-500/30"
            transition={{
              type: "spring",
              stiffness: 500, // Stiffer (snap faster)
              damping: 30
            }}
          />
        )}
        <motion.div
          className="relative z-10"
          animate={{
            scale: isActive ? 1.1 : 1,
            color: isActive ? '#ffffff' : '#9ca3af'
          }}
          whileHover={{ scale: 1.2 }}
          whileTap={{ scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
          {/* Notification Badge */}
          {showBadge && (
            <>
              <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 rounded-full animate-[notifPing_3s_ease-in-out_infinite]" />
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg shadow-red-500/40"
              >
                {item.badgeCount > 99 ? '99+' : item.badgeCount}
              </motion.span>
            </>
          )}
        </motion.div>
      </Link>
    );
  };

  const renderAvatar = () => (
    <div className="relative shrink-0">
      <div className="w-9 h-9 rounded-full bg-cyan-900/40 border border-cyan-500/30 flex items-center justify-center text-cyan-400 overflow-hidden">
        {user?.foto ? (
          <img
            src={user.foto}
            alt={user.username}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
          />
        ) : null}
        <User size={16} style={user?.foto ? { display: 'none' } : {}} />
      </div>
      <div
        className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-sura-blue animate-statusPulse"
      />
    </div>
  );

  // Animation variants
  const menuItemVariants = {
    hidden: { opacity: 0, x: -10 }, // Reduced distance
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.02, // Faster stagger (was 0.05)
        duration: 0.2, // Faster duration (was 0.3)
        ease: "easeOut"
      }
    })
  };

  return (
    <MotionConfig reducedMotion="user">
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? RAIL_WIDTH : FULL_WIDTH }}
      transition={COLLAPSE_TRANSITION}
      className="bg-sura-blue flex flex-col shrink-0 overflow-hidden"
      style={{ width: collapsed ? RAIL_WIDTH : FULL_WIDTH }}
    >
      {/* Header: Logo. Al colapsar, una ventana se cierra sobre la "S" de SURA
          mientras el logo se reduce: queda solo la S, a un tamaño legible. */}
      <motion.div
        initial={false}
        animate={{ height: collapsed ? 72 : 128 }}
        transition={COLLAPSE_TRANSITION}
        className="flex items-center justify-center border-b border-white/5 shrink-0"
      >
        <motion.div
          className="relative overflow-hidden transition-transform hover:scale-105"
          initial={false}
          animate={collapsed ? LOGO_MARK.frame : LOGO_FULL.frame}
          transition={COLLAPSE_TRANSITION}
        >
          <motion.img
            src={logo}
            alt="Centro SURA"
            className="absolute left-0 top-0 max-w-none"
            style={{ width: 'auto' }}
            initial={{ opacity: 0, ...(collapsed ? LOGO_MARK.img : LOGO_FULL.img) }}
            animate={{ opacity: 1, ...(collapsed ? LOGO_MARK.img : LOGO_FULL.img) }}
            transition={{ ...COLLAPSE_TRANSITION, opacity: { duration: 0.3 } }}
          />
        </motion.div>
      </motion.div>

      {/* Quick Access Toolbar */}
      <AutoHeight className="border-b border-white/5 shrink-0">
        <Swap swapKey={collapsed ? 'rail' : 'full'} delayIn={collapsed ? 0.04 : 0.12}>
          {collapsed ? (
            <div className="px-3 py-3" style={{ width: RAIL_WIDTH }}>
              <div className="flex flex-col items-center gap-1 bg-white/5 rounded-xl p-1.5">
                {quickAccessItems.map((item) => {
                  if (!hasPermission(item.roles, item.path)) return null;
                  return (
                    <RailTooltip key={item.path} label={item.label}>
                      {renderQuickAccessLink(item, 'quickAccessIndicatorRail', false)}
                    </RailTooltip>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="px-4 py-4" style={{ width: FULL_WIDTH }}>
              <div className="relative flex items-center justify-between bg-white/5 rounded-xl p-1.5">
                {quickAccessItems.map((item) => {
                  if (!hasPermission(item.roles, item.path)) return null;
                  return (
                    <React.Fragment key={item.path}>
                      {renderQuickAccessLink(item, 'quickAccessIndicator', true)}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </Swap>
      </AutoHeight>

      {/* Menu Scroll Area */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 no-scrollbar">
        <Swap swapKey={collapsed ? 'rail' : 'full'} delayIn={collapsed ? 0.04 : 0.12}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-1.5 px-3" style={{ width: RAIL_WIDTH }}>
              {menuStructure.map((group) => {
                if (group.hidden) return null;
                if (!group.items.some(item => hasPermission(item.roles, item.path))) return null;
                const GroupIcon = group.icon;
                const groupActive = group.items.some(item => hasPermission(item.roles, item.path) && isPathActive(item.path));

                return (
                  <RailTooltip key={group.key} label={group.title}>
                    <button
                      type="button"
                      onClick={() => openGroupFromRail(group.key)}
                      aria-label={`Abrir ${group.title}`}
                      className={`relative w-11 h-11 flex items-center justify-center rounded-xl transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 active:scale-95 ${groupActive ? 'bg-white/10 ring-1 ring-white/20 text-cyan-400' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                    >
                      {groupActive && (
                        <span className="absolute left-0 top-0 bottom-0 my-auto h-5 w-1 bg-cyan-400 rounded-r-full shadow-[0_0_10px_rgba(34,211,238,0.5)]" />
                      )}
                      <GroupIcon size={20} strokeWidth={groupActive ? 2.25 : 2} />
                    </button>
                  </RailTooltip>
                );
              })}
            </div>
          ) : (
            <div className="space-y-6 px-3" style={{ width: FULL_WIDTH }}>
              {menuStructure.map((group) => {
                if (group.hidden) return null;

                const groupHasVisibleItems = group.items.some(item => hasPermission(item.roles, item.path));

                // Si el grupo no tiene items visibles (por rol u ocultos), no mostrarlo.
                if (!groupHasVisibleItems) return null;

                return (
                  <div key={group.key} className="space-y-1">
                    <button
                      onClick={() => toggleGroup(group.key)}
                      className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-cyan-400/70 uppercase tracking-wider hover:text-cyan-300 transition-all hover:translate-x-1 active:scale-[0.98] group"
                    >
                      <span className="flex items-center gap-2 whitespace-nowrap">
                        {group.title}
                      </span>
                      <motion.div
                        animate={{ rotate: expandedGroups[group.key] ? 180 : 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                      >
                        <ChevronDown size={14} />
                      </motion.div>
                    </button>

                    <AnimatePresence initial={false}>
                      {expandedGroups[group.key] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="space-y-1 overflow-hidden"
                        >
                          {group.items.map((item, index) => {
                            if (!hasPermission(item.roles, item.path)) return null;
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;
                            const isPronto = item.path && isModuleMuyPronto(item.path);

                            return (
                              <motion.div
                                key={item.path}
                                custom={index}
                                initial="hidden"
                                animate="visible"
                                variants={menuItemVariants}
                              >
                                <Link
                                  to={item.path}
                                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-200 group relative"
                                >
                                  {/* Sliding background indicator */}
                                  {isActive && (
                                    <motion.div
                                      layoutId="menuActiveIndicator"
                                      className="absolute inset-0 bg-white/10 rounded-xl ring-1 ring-white/20"
                                      transition={{
                                        type: "spring",
                                        stiffness: 500, // Stiffer
                                        damping: 35 // More damping to prevent wobble
                                      }}
                                    />
                                  )}

                                  {/* Left accent bar */}
                                  {isActive && (
                                    <motion.div
                                      layoutId="menuAccentBar"
                                      className="absolute left-0 top-0 bottom-0 my-auto h-6 w-1 bg-cyan-400 rounded-r-full shadow-[0_0_10px_rgba(34,211,238,0.5)]"
                                      transition={{
                                        type: "spring",
                                        stiffness: 400,
                                        damping: 30
                                      }}
                                    />
                                  )}

                                  <div
                                    className="relative z-10 flex items-center gap-3 transition-transform hover:translate-x-1"
                                  >
                                    <Icon
                                      size={20}
                                      className={`
                                        flex-shrink-0 transition-colors duration-200
                                        ${isActive ? 'text-cyan-400' : 'text-gray-400 group-hover:text-white'}
                                      `}
                                    />

                                    <span className={`text-sm font-medium ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}>
                                      {item.label}
                                    </span>

                                    {isPronto && (
                                      <span className="ml-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide bg-cyan-500/15 text-cyan-300 border border-cyan-400/25">
                                        Pronto
                                      </span>
                                    )}
                                  </div>
                                </Link>
                              </motion.div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </Swap>
      </nav>

      {/* Herramientas sobre el panel del usuario, centradas: contraseña, ajustes,
          colapsar/expandir y, en la app de escritorio, Descargas. Colapsada, en columna. */}
      <div className={`shrink-0 border-t border-white/5 flex items-center justify-center pt-2.5 ${collapsed ? 'flex-col gap-0.5' : 'gap-2'}`}>
        <RailTooltip label="Cambiar contraseña" enabled={collapsed}>
          <button
            type="button"
            onClick={openPasswordModal}
            aria-label="Cambiar contraseña"
            title={collapsed ? undefined : 'Cambiar contraseña'}
            className={`${BOTON_HERRAMIENTA} hover:text-amber-400`}
          >
            <Lock size={16} />
          </button>
        </RailTooltip>

        {isPrivilegedRole(user?.role) && (
          <RailTooltip label="Ajustes del sistema" enabled={collapsed}>
            <Link
              to="/ajustes"
              aria-label="Ajustes del sistema"
              title={collapsed ? undefined : 'Ajustes del sistema'}
              className={`${BOTON_HERRAMIENTA} group/ajustes hover:text-cyan-400`}
            >
              <Settings size={18} className="transition-transform duration-300 group-hover/ajustes:rotate-90" />
            </Link>
          </RailTooltip>
        )}

        <RailTooltip label="Expandir menú" enabled={collapsed}>
          <button
            type="button"
            onClick={() => setCollapsed(c => !c)}
            aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            aria-expanded={!collapsed}
            title={collapsed ? undefined : 'Colapsar menú'}
            className={`${BOTON_HERRAMIENTA} hover:text-white`}
          >
            <BurgerIcon collapsed={collapsed} />
          </button>
        </RailTooltip>

        {hayDescargas && (
          <RailTooltip label="Descargas" enabled={collapsed}>
            <DescargasEscritorio />
          </RailTooltip>
        )}
      </div>

      {/* User Footer - Minimal & Subtle */}
      <AutoHeight className="shrink-0">
        <Swap swapKey={collapsed ? 'rail' : 'full'} delayIn={collapsed ? 0.04 : 0.12}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-0.5 pt-1 pb-4" style={{ width: RAIL_WIDTH }}>
              <RailTooltip label="Cerrar sesión">
                <button
                  onClick={logout}
                  aria-label="Cerrar sesión"
                  className="text-white/30 hover:text-red-400 transition-all p-2 rounded-lg hover:bg-white/5 hover:scale-110 hover:-rotate-12 active:scale-90"
                >
                  <LogOut size={18} />
                </button>
              </RailTooltip>

              <div className="mt-2">
                <RailTooltip label={user?.username || 'Usuario'}>
                  <div tabIndex={0} className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60">
                    {renderAvatar()}
                  </div>
                </RailTooltip>
              </div>
            </div>
          ) : (
            <div className="px-4 pt-2 pb-4" style={{ width: FULL_WIDTH }}>
              <div className="flex items-center justify-between">

                {/* User Identity */}
                <div
                  className="flex items-center gap-3 min-w-0 flex-1 transition-transform hover:scale-[1.02]"
                >
                  {renderAvatar()}

                  <div className="flex flex-col min-w-0">
                    <p className="text-sm font-medium text-white/90 truncate">
                      {user?.username || 'Usuario'}
                    </p>
                    <p className="text-[10px] font-medium text-white/40 uppercase tracking-wider truncate">
                      {getRoleLabel(user?.role)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  {/* Logout Action */}
                  <button
                    onClick={logout}
                    aria-label="Cerrar sesión"
                    title="Cerrar Sesión"
                    className="text-white/30 hover:text-red-400 transition-all p-2 rounded-lg hover:bg-white/5 hover:scale-110 hover:-rotate-12 active:scale-90"
                  >
                    <LogOut size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </Swap>
      </AutoHeight>
      {/* Change Password Modal */}
      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} size="sm" closeOnOverlay>
        <Modal.Header onClose={() => setShowPasswordModal(false)}>
          Cambiar Contraseña
        </Modal.Header>
        <form onSubmit={handleChangePassword}>
          <Modal.Body>
            <div className="flex flex-col gap-4">
              {/* Current Password */}
              <div className="form-group">
                <label className="label">Contraseña actual</label>
                <div className="relative">
                  <input
                    type={showCurrentPwd ? 'text' : 'password'}
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                    required
                    className="input pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                    aria-label={showCurrentPwd ? 'Ocultar contraseña actual' : 'Mostrar contraseña actual'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showCurrentPwd ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="form-group">
                <label className="label">Nueva contraseña</label>
                <div className="relative">
                  <input
                    type={showNewPwd ? 'text' : 'password'}
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm(p => ({ ...p, new: e.target.value }))}
                    required
                    className="input pr-10"
                    placeholder="Mínimo 4 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPwd(!showNewPwd)}
                    aria-label={showNewPwd ? 'Ocultar nueva contraseña' : 'Mostrar nueva contraseña'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showNewPwd ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label className="label">Confirmar contraseña</label>
                <input
                  type="password"
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                  required
                  className="input"
                  placeholder="Repite la nueva contraseña"
                />
              </div>

              {/* Error Message */}
              {passwordError && (
                <div className="error-message">
                  {passwordError}
                </div>
              )}

              {/* Success Message */}
              {passwordSuccess && (
                <div className="success-message">
                  <FaLock size={20} />
                  <span>¡Contraseña cambiada exitosamente!</span>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              onClick={() => setShowPasswordModal(false)}
              className="btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={passwordLoading || passwordSuccess}
              className="btn-primary"
            >
              {passwordLoading ? 'Cambiando...' : 'Cambiar Contraseña'}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </motion.aside>
    </MotionConfig>
  );
};

export default Sidebar;
